import React, { useEffect, useMemo, useState } from "react";
import SSBReportShell from "./SSBReportShell";
import SSBFiltersBar from "./SSBFiltersBar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

//const API_BASE = "http://localhost:9191";
const API_BASE =
  process.env.REACT_APP_SSB_API_BASE ||
  (window.location.hostname === "localhost"
    ? "http://localhost:9191"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app");

const money0 = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(Number(n));

const int0 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : new Intl.NumberFormat("en-US").format(Number(n));

const pct2 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(2)}%`;

const shortMonth = (m) => (m || "").slice(0, 10);

const Pill = ({ text, tone = "neutral" }) => {
  const map = {
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good: { bg: "#ECFDF3", fg: "#027A48" },
    warn: { bg: "#FFFAEB", fg: "#B54708" },
    bad: { bg: "#FEF3F2", fg: "#B42318" },
    info: { bg: "#EFF8FF", fg: "#175CD3" },
  };
  const c = map[tone] || map.neutral;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 900,
        border: "1px solid rgba(0,0,0,0.04)",
        letterSpacing: 0.2,
      }}
    >
      {text}
    </span>
  );
};

const Card = ({ title, value, sub }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #E7ECF3",
      borderRadius: 16,
      padding: 14,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
      minHeight: 88,
    }}
  >
    <div style={{ fontSize: 12, color: "#5E6B7A", marginBottom: 8, fontWeight: 700 }}>{title}</div>
    <div style={{ fontSize: 22, fontWeight: 900, color: "#0B1F33", lineHeight: 1.1 }}>{value}</div>
    {sub ? <div style={{ fontSize: 12, color: "#7A8696", marginTop: 6 }}>{sub}</div> : null}
  </div>
);

// canonical ordering
const BAND_ORDER = ["<1k", "1k-10k", "10k-50k", "50k+"];
const PERIOD_TYPES = ["month", "quarter"];

function normalizeBands(rows) {
  const map = {};

  for (const r of rows || []) {
    const band = r.amount_band;

    if (!map[band]) {
      map[band] = {
        amount_band: band,
        txn_count: 0,
        total_amount: 0,
      };
    }

    map[band].txn_count += Number(r.txn_count || 0);
    map[band].total_amount += Number(r.total_amount || 0);
  }

  return BAND_ORDER.map((b) => ({
    amount_band: b,
    txn_count: Number(map[b]?.txn_count || 0),
    total_amount: Number(map[b]?.total_amount || 0),
  }));
}

function buildPeriodMeta(months, periodType) {
  if (periodType === "month") {
    return months.map((m) => ({ label: shortMonth(m), months: [m] }));
  }
  const map = new Map();
  for (const m of months) {
    const year = m.slice(0, 4);
    const q = Math.ceil(parseInt(m.slice(5, 7)) / 3);
    const key = `${year}-Q${q}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  return Array.from(map.entries()).map(([label, ms]) => ({ label, months: ms }));
}

function aggregateBands(results) {
  const map = new Map();
  for (const r of results) {
    for (const row of r.rows || []) {
      const key = row.amount_band;
      const cur = map.get(key) || { amount_band: key, txn_count: 0, total_amount: 0 };
      cur.txn_count += Number(row.txn_count || 0);
      cur.total_amount += Number(row.total_amount || 0);
      map.set(key, cur);
    }
  }
  return { rows: Array.from(map.values()) };
}

function TooltipBar({ active, payload, metric }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const v = metric === "count" ? int0(p.txn_count) : money0(p.total_amount);
  const share = metric === "count" ? pct2(p.txn_share_pct) : pct2(p.amt_share_pct);
  return (
    <div
      style={{
        background: "white",
        border: "1px solid #E7ECF3",
        borderRadius: 12,
        padding: 10,
        boxShadow: "0 16px 40px rgba(16,24,40,0.12)",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 6 }}>{p.amount_band}</div>
      <div style={{ fontSize: 13, color: "#0B1F33" }}>
        <div>{metric === "count" ? "Txn Count" : "Total Volume"}: <b>{v}</b></div>
        <div>Share: <b>{share}</b></div>
      </div>
    </div>
  );
}

export default function SSBTicketSizeDistributionReport() {
  const [programs, setPrograms] = useState([]);
  const [months, setMonths] = useState([]);

  const [programId, setProgramId] = useState("");
  const [periodType, setPeriodType] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState("");

  const [metric, setMetric] = useState("amount");
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [err, setErr] = useState("");

  const [report, setReport] = useState(null);
  const [trend, setTrend] = useState([]);

  const periodMeta = useMemo(() => buildPeriodMeta(months, periodType), [months, periodType]);

  // Load filters
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingFilters(true);
      setErr("");
      try {
        const resp = await fetch(`${API_BASE}/v1/ssb/filters/ticket-bands`);
        const json = await resp.json();
        if (!mounted) return;

        const raw = Array.isArray(json.programs) ? json.programs : [];
        const p = raw.map((item) => typeof item === "string" ? { program_id: item, program_name: item } : item);
        const m = Array.isArray(json.months) ? json.months : [];

        setPrograms(p);
        setMonths(m);
      } catch (e) {
        console.error(e);
        if (mounted) setErr("Failed to load filters. Is the API running?");
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Init selectedPeriod when periodMeta changes
  useEffect(() => {
    if (periodMeta.length > 0) {
      setSelectedPeriod((prev) => {
        const stillValid = periodMeta.some((m) => m.label === prev);
        return stillValid ? prev : periodMeta[0].label;
      });
    }
  }, [periodMeta]);

  // Load report
  useEffect(() => {
    if (!selectedPeriod || periodMeta.length === 0) return;
    const meta = periodMeta.find((m) => m.label === selectedPeriod);
    if (!meta) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const results = await Promise.all(
          meta.months.map(async (monthStart) => {
            const url = new URL(`${API_BASE}/v1/ssb/ticket-bands`);
            url.searchParams.set("month_start", monthStart);
            if (programId) url.searchParams.set("program_id", programId);
            const resp = await fetch(url.toString());
            if (!resp.ok) return null;
            return resp.json();
          })
        );
        if (!mounted) return;
        const valid = results.filter(Boolean);
        const combined = valid.length === 1 ? valid[0] : aggregateBands(valid);
        setReport(combined);
      } catch (e) {
        console.error(e);
        if (mounted) setErr(`Failed to load report: ${e.message || e}`);
        if (mounted) setReport(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedPeriod, periodMeta, programId]);

  // Trend: 50k+ share across last 12 months (always monthly granularity)
  useEffect(() => {
    if (months.length === 0) { setTrend([]); return; }

    let cancelled = false;
    (async () => {
      try {
        const lastN = months.slice(0, 12);
        const results = await Promise.all(
          lastN.map(async (m) => {
            const url = new URL(`${API_BASE}/v1/ssb/ticket-bands`);
            url.searchParams.set("month_start", m);
            if (programId) url.searchParams.set("program_id", programId);
            const resp = await fetch(url.toString());
            if (!resp.ok) return null;
            const json = await resp.json();
            const rows = normalizeBands(json?.rows || []);
            const totalAmt = rows.reduce((s, r) => s + r.total_amount, 0);
            const totalCnt = rows.reduce((s, r) => s + r.txn_count, 0);
            const fifty = rows.find((r) => r.amount_band === "50k+") || { total_amount: 0, txn_count: 0 };
            return {
              month: shortMonth(m),
              amt_50k_share: totalAmt > 0 ? (fifty.total_amount / totalAmt) * 100 : 0,
              txn_50k_share: totalCnt > 0 ? (fifty.txn_count / totalCnt) * 100 : 0,
            };
          })
        );
        const out = results.filter(Boolean).sort((a, b) => (a.month > b.month ? 1 : -1));
        if (!cancelled) setTrend(out);
      } catch (e) {
        console.error("Trend load failed:", e);
      }
    })();

    return () => { cancelled = true; };
  }, [programId, months]);

  const rows = useMemo(() => normalizeBands(report?.rows || []), [report]);

  const totals = useMemo(() => {
    const total_amount = rows.reduce((s, r) => s + r.total_amount, 0);
    const txn_count = rows.reduce((s, r) => s + r.txn_count, 0);
    const fifty = rows.find((r) => r.amount_band === "50k+") || { total_amount: 0, txn_count: 0 };
    return {
      total_amount,
      txn_count,
      amt_50k_share: total_amount > 0 ? (fifty.total_amount / total_amount) * 100 : 0,
      txn_50k_share: txn_count > 0 ? (fifty.txn_count / txn_count) * 100 : 0,
    };
  }, [rows]);

  const tone = totals.amt_50k_share >= 90 ? "bad" : totals.amt_50k_share >= 70 ? "warn" : "good";
  const periodLabel = periodType === "quarter" ? "Quarter" : "Month";
  const programLabel = programId
    ? (programs.find((p) => p.program_id === programId)?.program_name || programId)
    : "All Programs";

  const banner = rows.length
    ? `High-value dominance: ${totals.amt_50k_share.toFixed(1)}% of volume is in 50k+ tickets`
    : "No data for selected filters.";

  const chartData = useMemo(() => {
    return rows.map((r) => {
      const amtShare = totals.total_amount > 0 ? (r.total_amount / totals.total_amount) * 100 : 0;
      const txnShare = totals.txn_count > 0 ? (r.txn_count / totals.txn_count) * 100 : 0;
      return {
        amount_band: r.amount_band,
        total_amount: r.total_amount,
        txn_count: r.txn_count,
        amt_share_pct: amtShare,
        txn_share_pct: txnShare,
        value: metric === "count" ? r.txn_count : r.total_amount,
      };
    });
  }, [rows, totals.total_amount, totals.txn_count, metric]);

  const toggleStyle = (active) => ({
    padding: "3px 9px",
    borderRadius: 6,
    border: `1px solid ${active ? "#3730A3" : "#D7DEE9"}`,
    background: active ? "#EEF2FF" : "#fff",
    color: active ? "#3730A3" : "#64748B",
    fontSize: 11,
    fontWeight: active ? 800 : 500,
    cursor: "pointer",
    lineHeight: 1.4,
  });

  return (
    <SSBReportShell
      title="SSB Dashboard — Ticket Size Distribution"
      subtitle={`Program: ${programLabel} • ${periodLabel}: ${selectedPeriod || "—"}`}
      rightMeta={[
        `Metric: ${metric === "count" ? "Txn Count" : "Volume ($)"}`,
        loading ? "Loading…" : "Live",
      ]}
      leftMeta={[
        "Sponsor bank oversight view",
        "Ticket size mix & high-value dominance",
      ]}
      footerLeft="Idenza • SSB Reporting (Phase 1)"
      footerRight="Source: mv_ssb_reports_ticket_bands_monthly"
    >
      {/* Filters */}
      <SSBFiltersBar>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>Program</div>
          <select
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
            disabled={loadingFilters}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="">All Programs</option>
            {programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>{p.program_name}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>Period</div>
            <div style={{ display: "flex", gap: 3 }}>
              {PERIOD_TYPES.map((t) => (
                <button key={t} onClick={() => setPeriodType(t)} style={toggleStyle(periodType === t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={loadingFilters || periodMeta.length === 0}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            {periodMeta.map((m) => (
              <option key={m.label} value={m.label}>{m.label}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>View</div>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="amount">Volume ($)</option>
            <option value="count">Txn Count</option>
          </select>
        </div>

        {/* Keep grid consistent (4 cols) */}
        <div />
      </SSBFiltersBar>

      {/* Banner */}
      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E7ECF3",
          borderRadius: 16,
          padding: 14,
          boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Pill text="High-Value Signal" tone={tone} />
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0B1F33" }}>{banner}</div>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>
            Total volume: <b>{money0(totals.total_amount)}</b> • Total txns: <b>{int0(totals.txn_count)}</b> • 50k+ txns: <b>{pct2(totals.txn_50k_share)}</b>
          </div>
        </div>
        <div>{loading ? <Pill text="Loading…" tone="neutral" /> : <Pill text="Live" tone="good" />}</div>
      </div>

      {/* Error */}
      {err ? (
        <div style={{ background: "#FEF3F2", border: "1px solid #FECACA", color: "#7F1D1D", padding: 12, borderRadius: 16, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        <Card title={`Total ${periodLabel} Volume`} value={money0(totals.total_amount)} />
        <Card title="Total Transactions" value={int0(totals.txn_count)} />
        <Card title="Volume in 50k+" value={pct2(totals.amt_50k_share)} sub="High-value dominance" />
        <Card title="Txns in 50k+" value={pct2(totals.txn_50k_share)} sub="Large ticket frequency" />
      </div>

      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 12, marginBottom: 12 }}>
        {/* Bar chart */}
        <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <div style={{ fontWeight: 900, color: "#0B1F33" }}>
              Ticket Band Breakdown — {metric === "count" ? "Txn Count" : "Volume ($)"}
            </div>
            <div style={{ fontSize: 12, color: "#64748B" }}>Hover for share %</div>
          </div>

          <div style={{ height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={(v) => (metric === "count" ? int0(v) : money0(v))}
                  style={{ fontSize: 11 }}
                />
                <YAxis type="category" dataKey="amount_band" width={90} style={{ fontSize: 12, fontWeight: 800 }} />
                <Tooltip content={<TooltipBar metric={metric} />} />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table */}
        <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
          <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 8 }}>Details</div>
          <div style={{ overflowX: "auto", border: "1px solid #F1F5F9", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #E7ECF3", background: "#F8FAFC" }}>
                  <th style={{ padding: 10 }}>Band</th>
                  <th style={{ padding: 10 }}>Txn Count</th>
                  <th style={{ padding: 10 }}>Volume</th>
                  <th style={{ padding: 10 }}>Txn Share</th>
                  <th style={{ padding: 10 }}>Vol Share</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((r) => {
                  const volTone = r.amount_band === "50k+" && totals.amt_50k_share >= 70 ? "warn" : "neutral";
                  return (
                    <tr key={r.amount_band} style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: 10, fontWeight: 900, color: "#0B1F33" }}>{r.amount_band}</td>
                      <td style={{ padding: 10 }}>{int0(r.txn_count)}</td>
                      <td style={{ padding: 10 }}>{money0(r.total_amount)}</td>
                      <td style={{ padding: 10 }}><Pill text={pct2(r.txn_share_pct)} tone="info" /></td>
                      <td style={{ padding: 10 }}><Pill text={pct2(r.amt_share_pct)} tone={volTone} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#64748B" }}>
            Sponsor-bank value: high ticket dominance helps prioritize where enhanced monitoring should start.
          </div>
        </div>
      </div>

      {/* Trend */}
      <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div style={{ fontWeight: 900, color: "#0B1F33" }}>50k+ Trend (last 12 months)</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>Volume share vs transaction share</div>
        </div>

        <div style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" style={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${Number(v).toFixed(0)}%`} style={{ fontSize: 11 }} />
              <Tooltip formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name]} />
              <Line type="monotone" dataKey="amt_50k_share" strokeWidth={3} dot={false} />
              <Line type="monotone" dataKey="txn_50k_share" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </SSBReportShell>
  );
}